/**
 * Geometric ↔ visual bounding box conversion.
 *
 * Two distinct boxes describe every shape:
 *
 * - **Geometric** — the rect that mathematically *generates* the shape, persisted as
 *   `x`/`y`/`width`/`height` on `ShapeData` (and `X`/`Y`/`W`/`H` on the wire). Some kinds use its
 *   corners directly (rectangle); others inscribe their vertices in the ellipse centered inside it
 *   (every regular polygon and star — see `regularPolygonCommands`).
 * - **Visual** — the tight rect containing all of the shape's vertices and paths, intersecting as
 *   many of them as possible. For a rectangle it equals the geometric box; for a triangle it is
 *   substantially smaller on both axes.
 *
 * The conversion is expressed as {@link VisualInsetRatios}: the visual box as fractions of the
 * geometric box. This works because vertex generation is *separable and linear* in `width`/`height`
 * — a polygon vertex is `(w/2 + w/2·sin θ, h/2 − h/2·cos θ)`, so `w` and `h` factor straight out.
 * Two consequences the rest of the editor depends on:
 *
 * 1. **Ratios are scale-invariant.** They depend only on the shape's *kind configuration*
 *    (type, sides, inset, custom path) — never on its current `width`/`height`. So a move or a
 *    resize never invalidates them; only an Object Effects edit or a path edit does. That keeps the
 *    trig off the geometry hot path entirely.
 * 2. **The conversion is exactly invertible.** {@link visualToGeometricRect} recovers the generator
 *    rect from a visual rect, which is what a gesture that manipulates the visual box needs.
 *
 * Bounds are path geometry only — stroke width is excluded. Callers needing the inked extent
 * inflate by half the stroke width themselves.
 *
 * Everything here is unrotated and in the shape's own axis-aligned frame. Rotation composes on top
 * as a separate step (see `canvasAabbOfRotatedPathBounds`), which keeps the ratios reusable.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsForSides } from '@/lib/elements/shape-path-commands'
import {
  SIDES_MAX,
  SIDES_MIN,
  innerRatioFromInsetPercent,
  isConvertedToRegularPolygon,
  normInset,
  normSides,
} from '@/lib/object-effects/sides'
import type { TightBounds } from './types/tight-bounds'
import { pathCommandsTightBounds } from './helpers/path-commands-tight-bounds'
import type { Rect } from './types/rect'
import type { VisualInsetRatios } from './types/visual-inset-ratios'

export const IDENTITY_VISUAL_INSET_RATIOS: VisualInsetRatios = { rx0: 0, ry0: 0, rx1: 1, ry1: 1 }

/**
 * Size the parametric path generators are sampled at to derive ratios. Any positive value yields
 * the same ratios (that is the scale-invariance above); a round number keeps the division exact for
 * the common cases and the intermediate coordinates well away from float-precision trouble.
 */
const RATIO_REFERENCE_SIZE = 1000

/** A span this small cannot be divided into meaningfully — treat the axis as degenerate. */
const MIN_RATIO_SPAN = 1e-9

function ratiosFromBounds(bounds: TightBounds, width: number, height: number): VisualInsetRatios {
  if (!(width > MIN_RATIO_SPAN) || !(height > MIN_RATIO_SPAN)) {
    return IDENTITY_VISUAL_INSET_RATIOS
  }

  return {
    rx0: bounds.minX / width,
    ry0: bounds.minY / height,
    rx1: bounds.maxX / width,
    ry1: bounds.maxY / height,
  }
}

/**
 * Closed-form visual ratios for a regular polygon or star, derived directly from the vertex
 * formula in `regularPolygonCommands` rather than by sampling a generated path.
 *
 * Outer vertices sit on the unit circle at `θ_i = (360/sides)·i` for `i = 1..sides`; when
 * `insetPercent > 0` an inward "valley" vertex is inserted between each pair at half-step offset,
 * on a circle of radius `cos(180/sides)·(1 − inset/100)`. Both sets are considered because for a
 * high inset the valley vertices can reach further along an axis than any outer tip does.
 *
 * `sides` and `insetPercent` are normalized exactly as `regularPolygonCommands` normalizes them, so
 * the two agree for every input (asserted in the tests).
 */
export function regularPolygonVisualInsetRatios(
  sidesIn: number,
  insetPercent: number
): VisualInsetRatios {
  const sides = Math.max(SIDES_MIN, Math.min(SIDES_MAX, Math.round(sidesIn)))
  const inset = Math.max(0, insetPercent)
  const halfStepDeg = 360 / sides / 2
  const apothemRatio = Math.cos((halfStepDeg * Math.PI) / 180)
  const innerRadius = apothemRatio * (1 - inset / 100)

  let sxMin = Infinity
  let sxMax = -Infinity
  let syMin = Infinity
  let syMax = -Infinity

  const add = (sx: number, sy: number) => {
    if (sx < sxMin) sxMin = sx
    if (sx > sxMax) sxMax = sx
    if (sy < syMin) syMin = sy
    if (sy > syMax) syMax = sy
  }

  for (let i = 1; i <= sides; i++) {
    const angleDeg = (360 / sides) * i
    const angleRad = (angleDeg * Math.PI) / 180

    add(Math.sin(angleRad), -Math.cos(angleRad))

    if (inset > 0) {
      const innerAngleRad = ((angleDeg + halfStepDeg) * Math.PI) / 180

      add(innerRadius * Math.sin(innerAngleRad), -innerRadius * Math.cos(innerAngleRad))
    }
  }

  // Unit-circle coords are centered on the rect's midpoint: offset -1..1 maps to 0..1 of the rect.
  return {
    rx0: (1 + sxMin) / 2,
    ry0: (1 + syMin) / 2,
    rx1: (1 + sxMax) / 2,
    ry1: (1 + syMax) / 2,
  }
}

/** Ratio cache for parametric kinds, keyed by kind configuration (never by size). */
const parametricRatioCache = new Map<string, VisualInsetRatios>()

/**
 * Tight-bounds cache for explicit custom paths. Keyed on the command array identity — `ShapeData`
 * treats `pathCommands` as immutable, so a new array means new geometry. Raw bounds are cached
 * rather than ratios because a custom path's coordinates are baked at whatever size it was authored
 * at, so the division by the shape's current width/height must happen per call.
 */
const customPathBoundsCache = new WeakMap<PathCommand[], TightBounds | null>()

function customPathTightBounds(commands: PathCommand[]): TightBounds | null {
  if (customPathBoundsCache.has(commands)) {
    return customPathBoundsCache.get(commands) ?? null
  }

  const bounds = pathCommandsTightBounds(commands)

  customPathBoundsCache.set(commands, bounds)
  return bounds
}

function parametricVisualInsetRatios(shape: ShapeData): VisualInsetRatios {
  const sides = normSides(shape)
  const inset = normInset(shape)
  const asRegularPolygon = isConvertedToRegularPolygon(shape)
  const key = `${shape.type}|${sides}|${inset}|${asRegularPolygon ? 1 : 0}`
  const cached = parametricRatioCache.get(key)

  if (cached) return cached

  // Mirrors the fallback branch of `resolveLocalPathCommands` (`lib/text/shape-contour-wrap.ts`)
  // exactly — same generator, same arguments — but sampled at a fixed reference size so the result
  // is cacheable per configuration instead of per size.
  const commands = pathCommandsForSides(
    shape.type,
    sides,
    RATIO_REFERENCE_SIZE,
    RATIO_REFERENCE_SIZE,
    innerRatioFromInsetPercent(inset),
    { asRegularPolygon }
  )
  const bounds = pathCommandsTightBounds(commands)
  const ratios = bounds
    ? ratiosFromBounds(bounds, RATIO_REFERENCE_SIZE, RATIO_REFERENCE_SIZE)
    : IDENTITY_VISUAL_INSET_RATIOS

  parametricRatioCache.set(key, ratios)
  return ratios
}

/**
 * Visual ratios for a shape's current kind configuration.
 *
 * Resolution order matches `resolveLocalPathCommands`: an explicit custom path (irregular polygon
 * kindId 2, pen path kindId 9) always wins; otherwise the geometry is regenerated from
 * type/sides/inset. An empty command array falls through to regeneration rather than being treated
 * as real (empty) geometry.
 *
 * Cheap and safe to call per shape per frame. Callers looping over many shapes every frame should
 * still hoist this out of the loop — the result only changes when the shape's kind configuration
 * does, never when it moves, resizes, or rotates.
 */
export function visualInsetRatiosForShape(shape: ShapeData): VisualInsetRatios {
  const custom = shape.pathCommands

  if (custom && custom.length > 0) {
    const bounds = customPathTightBounds(custom)

    return bounds
      ? ratiosFromBounds(bounds, shape.width, shape.height)
      : IDENTITY_VISUAL_INSET_RATIOS
  }

  return parametricVisualInsetRatios(shape)
}

/** Geometric rect → visual rect. */
export function geometricToVisualRect(geometric: Rect, ratios: VisualInsetRatios): Rect {
  return {
    x: geometric.x + ratios.rx0 * geometric.width,
    y: geometric.y + ratios.ry0 * geometric.height,
    width: (ratios.rx1 - ratios.rx0) * geometric.width,
    height: (ratios.ry1 - ratios.ry0) * geometric.height,
  }
}

/**
 * Visual rect → geometric rect: the exact inverse of {@link geometricToVisualRect}.
 *
 * Use when a gesture or a panel field manipulates the visual box and the generator rect has to be
 * recovered. A degenerate axis (a shape whose geometry collapses to zero extent along it, so no
 * visual span can be attributed back to a geometric one) passes that axis through unchanged rather
 * than dividing by ~zero and producing an infinite rect.
 */
export function visualToGeometricRect(visual: Rect, ratios: VisualInsetRatios): Rect {
  const spanX = ratios.rx1 - ratios.rx0
  const spanY = ratios.ry1 - ratios.ry0
  const width = spanX > MIN_RATIO_SPAN ? visual.width / spanX : visual.width
  const height = spanY > MIN_RATIO_SPAN ? visual.height / spanY : visual.height

  return {
    x: visual.x - ratios.rx0 * width,
    y: visual.y - ratios.ry0 * height,
    width,
    height,
  }
}

/** The shape's geometric rect — its persisted generator box. */
export function geometricRectOfShape(shape: ShapeData): Rect {
  return { x: shape.x, y: shape.y, width: shape.width, height: shape.height }
}

/**
 * The shape's visual rect in canvas space, unrotated.
 *
 * For a rotated shape this is still the axis-aligned box in the shape's own frame; compose rotation
 * separately to get a canvas-space AABB.
 */
export function visualRectOfShape(shape: ShapeData): Rect {
  return geometricToVisualRect(geometricRectOfShape(shape), visualInsetRatiosForShape(shape))
}
